/**
 * Unified Device Software Inventory model for Remote Console.
 *
 * Installed = device-reported PackageManager identity (never fabricated).
 * Available = active approved release / SSM catalog.
 * Update is eligible only when inventory is fresh enough.
 */

export const PLAYER_PACKAGE_NAME = "com.krosskast";
export const ASSISTANT_PACKAGE_NAME = "com.krosskast.assistant";
export const SCREENSAVER_PACKAGE_NAME = "com.krosskast.screensaver";

export const REFRESH_SOFTWARE_INVENTORY_COMMAND =
  "refresh_software_inventory" as const;

/** Max age for device-reported inventory to authorize Update decisions. */
export const SOFTWARE_INVENTORY_FRESH_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type SoftwarePackageIdentity = {
  versionCode: number;
  versionName: string;
  displayVersion?: string | null;
};

export type InventoryFreshness = "missing" | "fresh" | "stale" | "legacy";

export type SoftwarePackageStatusInput = {
  installed?: SoftwarePackageIdentity | null;
  available?: SoftwarePackageIdentity | null;
  updateAvailable?: boolean;
  upToDate?: boolean;
  /** ISO-8601 observation time from Fleet inventory. */
  observedAt?: string | null;
  lastReportedAt?: string | null;
  inventoryFresh?: boolean | null;
  inventoryStale?: boolean | null;
  inventory?: {
    observedAt?: string | null;
    fresh?: boolean | null;
    stale?: boolean | null;
    /** Fleet ingest source (boot | package_replaced | refresh | reconnect | …). */
    source?: string | null;
  } | null;
  /** Top-level source when Fleet mirrors inventory.source. */
  inventorySource?: string | null;
};

export function displaySoftwareVersion(
  versionName: string | null | undefined
): string | null {
  if (typeof versionName !== "string" || !versionName.trim()) return null;
  const raw = versionName.trim();
  const m = raw.match(/(\d{4}\.\d{1,2}\.\d{1,2}\.\d+)\s*$/);
  return m ? m[1] : raw;
}

export function isSoftwareUpdateAvailable(args: {
  installedVersionCode?: number | null;
  availableVersionCode?: number | null;
}): boolean {
  const available = Number(args.availableVersionCode);
  if (!Number.isFinite(available)) return false;
  const installed = Number(args.installedVersionCode);
  if (!Number.isFinite(installed)) return true;
  return installed < available;
}

export function isSoftwareUpToDate(args: {
  installedVersionCode?: number | null;
  availableVersionCode?: number | null;
}): boolean {
  const installed = Number(args.installedVersionCode);
  const available = Number(args.availableVersionCode);
  return (
    Number.isFinite(installed) &&
    Number.isFinite(available) &&
    installed === available
  );
}

function parseObservedAtMs(
  value: string | null | undefined
): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export function resolveInventoryObservedAt(
  status: SoftwarePackageStatusInput | null | undefined
): string | null {
  if (!status) return null;
  const candidates = [
    status.observedAt,
    status.lastReportedAt,
    status.inventory?.observedAt,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

/**
 * Resolve inventory freshness without fabricating installed identity.
 * - missing: no installed package identity
 * - fresh/stale: from Fleet flags or observedAt vs TTL
 * - legacy: installed present but Fleet has not yet attached inventory metadata
 *   (OTA-persisted rows). Display may show the version; Update still requires
 *   fresh inventory once freshness fields exist.
 */
export function resolveInventoryFreshness(
  status: SoftwarePackageStatusInput | null | undefined,
  nowMs: number = Date.now(),
  maxAgeMs: number = SOFTWARE_INVENTORY_FRESH_MAX_AGE_MS
): InventoryFreshness {
  if (!status?.installed) return "missing";

  const fleetStale =
    status.inventoryStale === true || status.inventory?.stale === true;
  const fleetFresh =
    status.inventoryFresh === true || status.inventory?.fresh === true;
  if (fleetStale || status.inventoryFresh === false) return "stale";
  if (fleetFresh) return "fresh";

  const observedAt = resolveInventoryObservedAt(status);
  const observedMs = parseObservedAtMs(observedAt);
  if (observedMs != null) {
    return nowMs - observedMs <= maxAgeMs ? "fresh" : "stale";
  }

  // Installed without observation metadata — do not invent freshness.
  return "legacy";
}

/**
 * Installed Version primary line — PackageManager display identity only.
 * Verification nuance lives in {@link formatInventoryVerificationCaption}.
 */
export function formatInstalledVersionLabel(args: {
  status: SoftwarePackageStatusInput | null | undefined;
  loading?: boolean;
  singleZone?: boolean;
  nowMs?: number;
}): string {
  if (args.loading) return "Loading…";
  if (args.singleZone === false) return "Select exactly one Zone";

  const freshness = resolveInventoryFreshness(args.status, args.nowMs);
  if (freshness === "missing") {
    return "Unknown — not yet reported by device";
  }

  const display =
    args.status?.installed?.displayVersion ||
    displaySoftwareVersion(args.status?.installed?.versionName) ||
    null;

  if (!display) {
    return "Unknown — not yet reported by device";
  }

  return display;
}

/** Human relative age from observedAt; never invents a time. */
export function formatInventoryRelativeAge(
  observedAt: string | null | undefined,
  nowMs: number = Date.now()
): string | null {
  const ms = parseObservedAtMs(observedAt);
  if (ms == null) return null;
  const delta = Math.max(0, nowMs - ms);
  const sec = Math.floor(delta / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return min === 1 ? "1 minute ago" : `${min} minutes ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return hr === 1 ? "1 hour ago" : `${hr} hours ago`;
  const day = Math.floor(hr / 24);
  return day === 1 ? "1 day ago" : `${day} days ago`;
}

export type InventoryVerificationCaption = {
  text: string;
  /** Exact Fleet observedAt for tooltip / title. */
  exactAt: string | null;
  kind: "verified" | "last_reported" | "ota_history" | "unknown" | "none";
};

/**
 * Subtle line under Installed Version. Advances only with device-confirmed
 * Fleet observedAt — browser polls alone never invent a newer stamp.
 */
export function formatInventoryVerificationCaption(args: {
  status: SoftwarePackageStatusInput | null | undefined;
  loading?: boolean;
  singleZone?: boolean;
  nowMs?: number;
}): InventoryVerificationCaption | null {
  if (args.loading || args.singleZone === false) return null;

  const freshness = resolveInventoryFreshness(args.status, args.nowMs);
  if (freshness === "missing") {
    return { text: "Unknown", exactAt: null, kind: "unknown" };
  }

  const observedAt = resolveInventoryObservedAt(args.status);
  const relative = formatInventoryRelativeAge(observedAt, args.nowMs);
  const exactAt = formatInventoryTimestamp(observedAt);

  if (freshness === "stale") {
    return {
      text: relative ? `Last reported · ${relative}` : "Last reported",
      exactAt,
      kind: "last_reported",
    };
  }
  if (freshness === "legacy") {
    return {
      text: "OTA history · not PackageManager inventory",
      exactAt: null,
      kind: "ota_history",
    };
  }

  // fresh PackageManager inventory (boot / package_replaced / refresh / …)
  return {
    text: relative ? `Device verified · ${relative}` : "Device verified",
    exactAt,
    kind: "verified",
  };
}

/** Compact UTC stamp for stale Installed labels (never invents a time). */
export function formatInventoryTimestamp(
  observedAt: string | null | undefined
): string | null {
  const ms = parseObservedAtMs(observedAt);
  if (ms == null) return null;
  try {
    return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
  } catch {
    return null;
  }
}

/**
 * Update is allowed only when a newer Available exists and inventory is
 * fresh enough. Legacy OTA-persisted rows (no observation metadata) keep
 * prior Update behavior so staging is not bricked before Fleet ships
 * freshness fields — once Fleet marks stale/fresh, those flags win.
 */
export function isSoftwareUpdateEligible(args: {
  status: SoftwarePackageStatusInput | null | undefined;
  nowMs?: number;
}): boolean {
  const status = args.status;
  if (!status) return false;

  // Version-code truth always wins over a stale Fleet updateAvailable flag
  // (never offer Update that would be a downgrade).
  const newerAvailable = isSoftwareUpdateAvailable({
    installedVersionCode: status.installed?.versionCode ?? null,
    availableVersionCode: status.available?.versionCode ?? null,
  });
  if (!newerAvailable) return false;

  const updateAvailable =
    typeof status.updateAvailable === "boolean"
      ? status.updateAvailable
      : true;
  if (!updateAvailable) return false;

  const freshness = resolveInventoryFreshness(status, args.nowMs);
  if (freshness === "missing" || freshness === "stale") return false;
  // fresh or legacy (no freshness metadata yet)
  return true;
}

export function isSoftwareStatusUpToDate(
  status: SoftwarePackageStatusInput | null | undefined
): boolean {
  if (!status) return false;
  if (typeof status.upToDate === "boolean") return status.upToDate;
  return isSoftwareUpToDate({
    installedVersionCode: status.installed?.versionCode ?? null,
    availableVersionCode: status.available?.versionCode ?? null,
  });
}

export function formatAvailableVersionLabel(
  status: SoftwarePackageStatusInput | null | undefined
): string {
  return (
    status?.available?.displayVersion ||
    displaySoftwareVersion(status?.available?.versionName) ||
    "None configured"
  );
}

/** Exact Fleet command body — requests a fresh device inventory report. */
export function buildRefreshSoftwareInventoryBody(): {
  command: typeof REFRESH_SOFTWARE_INVENTORY_COMMAND;
} {
  return { command: REFRESH_SOFTWARE_INVENTORY_COMMAND };
}

export function buildPlayerPackageUpdateBody(): {
  command: "update_kk_app";
} {
  return { command: "update_kk_app" };
}
